import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import ScheduleTaskCard from './ScheduleTaskCard';
import { format } from 'date-fns';
import { Plus } from 'lucide-react';

function ScheduleBoardCell({ date, workCenter, schedules, settledDateMap, scheduleChildrenMap, scheduleDescendantCountMap, scheduleOrderTotalsMap, onDeleteSchedule, onSplitSchedule, onWithdrawSplit, onExportProcess, onEditNote, onQuickAdd, rowColor }) {
  const dateKey = format(date, 'yyyy-MM-dd');
  const cellId = `cell-${workCenter._id}-${dateKey}`;
  const isSettled = !!settledDateMap?.[dateKey];

  const { setNodeRef, isOver } = useDroppable({
    id: cellId,
    disabled: isSettled,
    data: {
      type: 'board-cell',
      workCenterId: workCenter._id,
      workCenterName: workCenter.name,
      date,
      dateKey,
    },
  });

  const totalQty = schedules?.reduce((sum, s) => sum + (s.assigned_quantity || 0), 0) || 0;
  const totalTime = schedules?.reduce((sum, s) => sum + (s.standard_time || 0), 0) || 0;

  const sortableItems = schedules?.map((s) => `board-task-${s._id}`) || [];

  return (
    <div
      ref={setNodeRef}
      className={`
        relative group w-[var(--date-col-width)] shrink-0 p-1 sm:p-1.5 border-r border-b border-gray-200
        transition-colors
        ${isOver ? 'bg-blue-50' : rowColor ? `${rowColor.bg} hover:brightness-[0.97]` : 'bg-white hover:bg-gray-50'}
      `}
      style={{ minHeight: 140 }}
    >
      {schedules?.length > 0 && (
        <div className="flex items-center justify-between mb-1 px-0.5">
          <span className="text-[10px] text-gray-400">
            {schedules.length}项 · {totalQty}件
          </span>
          {totalTime > 0 && (
            <span className="text-[10px] text-gray-400">{totalTime}分</span>
          )}
        </div>
      )}
      <SortableContext
        items={sortableItems}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-1">
          {schedules?.map((schedule) => {
            const children = scheduleChildrenMap?.[schedule._id] || [];
            const descendantCount = scheduleDescendantCountMap?.[schedule._id] || 0;
            return (
              <ScheduleTaskCard
                key={schedule._id}
                schedule={schedule}
                orderTotals={scheduleOrderTotalsMap?.[schedule._id]}
                disabled={isSettled}
                hasChildren={children.length > 0}
                childCount={children.length}
                descendantCount={descendantCount}
                onDelete={onDeleteSchedule}
                onSplit={onSplitSchedule}
                onWithdrawSplit={onWithdrawSplit}
                onExportProcess={onExportProcess}
                onEditNote={onEditNote}
              />
            );
          })}
        </div>
      </SortableContext>

      {isOver && (
        <div className="absolute inset-0 z-10 border-2 border-blue-400 pointer-events-none" />
      )}

      {!isSettled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onQuickAdd?.(workCenter, date);
          }}
          className="absolute top-1 right-1 z-20 p-1 rounded-md bg-white/80 text-gray-400
                     opacity-0 group-hover:opacity-100 transition-opacity
                     hover:text-blue-600 hover:bg-blue-50 shadow-sm border border-gray-200"
          title="快速添加任务"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default ScheduleBoardCell;
